'use client'

import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import Script from 'next/script'
import { ChevronRight, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import posthog from 'posthog-js'

const ISSUE_TYPES = [
  'Broken link or page',
  'Something looks wrong',
  'Incorrect information',
  'Accessibility problem',
  'Other',
]

const EMPTY_FORM = {
  name: '',
  email: '',
  pageUrl: '',
  issueType: ISSUE_TYPES[0],
  description: '',
}

export default function ReportAnIssuePage() {
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  // Prefill the page URL with wherever the visitor came from, if available.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const fromParam = params.get('url')
    const referrer = document.referrer && !document.referrer.includes('/report-an-issue')
      ? document.referrer
      : ''
    const prefill = fromParam || referrer
    if (prefill) {
      setFormData((prev) => ({ ...prev, pageUrl: prefill }))
    }
  }, [])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      const token = await new Promise<string>((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!, { action: 'submit_issue' })
            .then(resolve)
            .catch(reject)
        })
      })

      const res = await fetch('/api/submit-issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, formData }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Submission failed')
      }

      setSubmitted(true)
      posthog.capture('issue_reported', { issue_type: formData.issueType })
    } catch (err) {
      console.error('Error submitting issue:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen px-6 md:px-12">
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
        strategy="afterInteractive"
      />

      {/* Breadcrumb */}
      <div className="max-w-3xl mx-auto pt-42 pb-6">
        <nav className="flex items-center gap-2 text-sm text-gray-600">
          <Link href="/" className="hover:text-brand-blue transition-colors">
            Home
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-medium">Report an Issue</span>
        </nav>
      </div>

      <div className="max-w-3xl mx-auto pb-20">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium text-brand-blue mb-3">
            Report an Issue
          </h1>
          <p className="text-gray-600 text-lg leading-relaxed">
            Spotted a broken link, a typo, or something that doesn&apos;t look right? Let us know and
            we&apos;ll get it fixed. For legal questions, please{' '}
            <Link href="/contact" className="text-brand-blue font-medium hover:underline">
              contact us
            </Link>{' '}
            instead.
          </p>
        </div>

        {submitted ? (
          <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-gray-900 mb-2">Thank you!</h2>
            <p className="text-gray-600 mb-6">
              Your report has been sent to our team. We appreciate you helping us improve the site.
            </p>
            <button
              onClick={() => {
                setSubmitted(false)
                setFormData(EMPTY_FORM)
              }}
              className="text-brand-blue font-medium hover:underline"
            >
              Report another issue
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all bg-white"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all bg-white"
                  placeholder="jane@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="pageUrl" className="block text-sm font-medium text-gray-700 mb-1.5">
                Page where you saw the issue{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                id="pageUrl"
                name="pageUrl"
                value={formData.pageUrl}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all bg-white"
                placeholder="https://serottelaw.com/..."
              />
            </div>

            <div>
              <label htmlFor="issueType" className="block text-sm font-medium text-gray-700 mb-1.5">
                What kind of issue is it?
              </label>
              <select
                id="issueType"
                name="issueType"
                value={formData.issueType}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all bg-white"
              >
                {ISSUE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                Describe the issue
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all resize-none bg-white"
                placeholder="Tell us what happened and what you expected to see."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-brand-blue text-white py-3.5 rounded-lg font-medium hover:bg-brand-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                'Submit Report'
              )}
            </button>

            {error && (
              <p className="flex items-center justify-center gap-2 text-red-600 text-sm text-center">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </p>
            )}

            <p className="text-xs text-gray-500 text-center">
              This site is protected by reCAPTCHA and the Google{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-700"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-700"
              >
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
